import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PlusCircle, Flame } from "lucide-react";
import api, { fmtMoney } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Kpi, PageHeader } from "@/components/Primitives";
import { BookingsTable } from "@/components/BookingsTable";
import { Button } from "@/components/ui/button";

export function AgentDashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dashboard/agent").then((r) => setD(r.data)); }, []);
  if (!d) return <div className="text-slate-500">Loading…</div>;

  return (
    <div data-testid="agent-dashboard">
      <PageHeader eyebrow={new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })} title={`Hello, ${user.name.split(" ")[0]}`}>
        <Button asChild className="bg-indigo-600 hover:bg-indigo-500"><Link to="/bookings/new" data-testid="dashboard-new-booking-btn"><PlusCircle className="h-4 w-4 mr-2" />New Booking</Link></Button>
      </PageHeader>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Kpi testId="kpi-today-bookings" label="Today · Bookings" value={d.today.count} sub={`${fmtMoney(d.today.gross)} gross`} />
        <div className={`panel p-5 fade-up relative overflow-hidden ${d.daily.hit ? "border-emerald-500/40" : ""}`} data-testid="kpi-daily-goal">
          <div className="flex items-center justify-between"><div className="eyebrow">Today · Daily MCO goal</div>{d.daily.hit && <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-400" data-testid="daily-goal-hit">Goal hit</span>}</div>
          <div className="font-display text-3xl font-bold mt-2 text-emerald-400 font-mono" data-testid="daily-mco">{fmtMoney(d.daily.mco)}</div>
          <div className="mt-3 h-2 rounded-full bg-slate-800 overflow-hidden">
            <div className={`h-full transition-[width] duration-700 ${d.daily.hit ? "bg-emerald-400" : "bg-indigo-500"}`} style={{ width: `${d.daily.progress}%` }} data-testid="daily-progress-bar" />
          </div>
          <div className="flex justify-between text-xs text-slate-500 mt-1"><span data-testid="daily-progress-pct">{d.daily.progress}%</span><span>{d.daily.hit ? `goal ${fmtMoney(d.daily.target)}` : `${fmtMoney(d.daily.remaining)} to go · ${fmtMoney(d.daily.target)}`}</span></div>
        </div>
        <div className="panel p-5 fade-up" data-testid="kpi-streak">
          <div className="eyebrow">Goal streak</div>
          <div className="flex items-end gap-2 mt-2">
            <Flame className={`h-7 w-7 mb-1 ${d.daily.streak > 0 ? "text-orange-400" : "text-slate-700"}`} />
            <span className="font-display text-3xl font-bold" data-testid="streak-count">{d.daily.streak}</span>
            <span className="text-sm text-slate-500 mb-1">day{d.daily.streak === 1 ? "" : "s"}</span>
          </div>
          <div className="flex items-center gap-1.5 mt-3" data-testid="streak-last7">
            {d.daily.last7.map((x) => (
              <div key={x.date} title={`${x.date}: ${fmtMoney(x.mco)}`} className={`h-2.5 flex-1 rounded-sm ${x.hit ? "bg-emerald-400" : x.mco > 0 ? "bg-slate-600" : "bg-slate-800"}`} />
            ))}
          </div>
          <div className="flex justify-between text-xs text-slate-500 mt-1"><span>last 7 days</span><span data-testid="best-streak">best {d.daily.best_streak}</span></div>
        </div>
        <div className="panel p-5 fade-up" data-testid="kpi-mtd-target">
          <div className="eyebrow">MTD · MCO vs Target</div>
          <div className="font-display text-3xl font-bold mt-2 text-emerald-400 font-mono">{fmtMoney(d.mtd.mco)}</div>
          <div className="mt-3 h-2 rounded-full bg-slate-800 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-indigo-500 to-emerald-400 transition-[width] duration-700" style={{ width: `${d.mtd.progress}%` }} data-testid="target-progress-bar" />
          </div>
          <div className="flex justify-between text-xs text-slate-500 mt-1"><span data-testid="target-progress-pct">{d.mtd.progress}% of target</span><span>{d.mtd.count} bookings · {fmtMoney(d.mtd.target)}</span></div>
        </div>
      </div>

      <div className="flex gap-3 mb-6 text-xs">
        <span className="rounded border border-amber-500/30 bg-amber-500/10 text-amber-300 px-3 py-1.5" data-testid="pending-count-chip">{d.pending_count} awaiting Payment Desk</span>
        {d.declined_count > 0 && <span className="rounded border border-rose-500/30 bg-rose-500/10 text-rose-300 px-3 py-1.5" data-testid="declined-count-chip">{d.declined_count} declined / failed — needs attention</span>}
      </div>

      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-lg font-semibold">Recent sales</h2>
        <Link to="/bookings" className="text-sm text-indigo-300 hover:text-indigo-200" data-testid="view-all-bookings-link">View all →</Link>
      </div>
      <BookingsTable rows={d.recent} testId="recent-bookings-table" emptyText="No bookings yet. Create your first one." />
    </div>
  );
}
